import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Pencil, Trash2, X, Check, ShieldAlert, RotateCw, Music, Calendar, FileText, AlertTriangle, XCircle, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PERMIT_TYPES = ['DJ', 'Band', 'Karaoke', 'Others'];

const formatDate = (d) => {
  if (!d) return '--';
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return d; }
};

const DctLicensing = () => {
  const [permits, setPermits] = useState([]);
  const [entertainers, setEntertainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('permits');
  const [showPermitForm, setShowPermitForm] = useState(false);
  const [showEntertainerForm, setShowEntertainerForm] = useState(false);
  const [editingPermit, setEditingPermit] = useState(null);
  const [editingEntertainer, setEditingEntertainer] = useState(null);
  const [renewId, setRenewId] = useState(null);
  const [renewDate, setRenewDate] = useState('');
  const [includeClosed, setIncludeClosed] = useState(false);

  const downloadPermits = (format) => {
    const url = `${API}/api/dct-licensing/permits/download?format=${format}&include_closed=${includeClosed}`;
    window.open(url, '_blank');
    toast({ title: `Downloading permits ${format.toUpperCase()}`, description: includeClosed ? 'Includes closed permits' : 'Active permits only' });
  };

  const downloadEntertainers = (format) => {
    const url = `${API}/api/dct-licensing/entertainers/download?format=${format}`;
    window.open(url, '_blank');
    toast({ title: `Downloading entertainers ${format.toUpperCase()}` });
  };

  const loadData = useCallback(async () => {
    try {
      const [pRes, eRes] = await Promise.all([
        axios.get(`${API}/api/dct-licensing/permits`),
        axios.get(`${API}/api/dct-licensing/entertainers`),
      ]);
      setPermits(pRes.data.permits || []);
      setEntertainers(eRes.data.entertainers || []);
    } catch (err) { console.error(err); } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const deletePermit = async (id) => {
    if (!window.confirm('Delete this permit?')) return;
    await axios.delete(`${API}/api/dct-licensing/permits/${id}`);
    setPermits(p => p.filter(x => x.id !== id));
    toast({ title: 'Permit deleted' });
  };

  const closePermit = async (id) => {
    await axios.post(`${API}/api/dct-licensing/permits/${id}/close`);
    setPermits(p => p.map(x => x.id === id ? { ...x, status: 'closed' } : x));
    toast({ title: 'Permit closed' });
  };

  const reactivatePermit = async (id) => {
    await axios.post(`${API}/api/dct-licensing/permits/${id}/reactivate`);
    setPermits(p => p.map(x => x.id === id ? { ...x, status: 'active' } : x));
    toast({ title: 'Permit reactivated' });
  };

  const handleRenew = async () => {
    if (!renewDate) return;
    const res = await axios.patch(`${API}/api/dct-licensing/permits/${renewId}/renew?new_expiry_date=${renewDate}`);
    setPermits(p => p.map(x => x.id === renewId ? res.data : x));
    setRenewId(null);
    setRenewDate('');
    toast({ title: 'Permit renewed' });
  };

  const deleteEntertainer = async (id) => {
    if (!window.confirm('Delete this entertainer?')) return;
    await axios.delete(`${API}/api/dct-licensing/entertainers/${id}`);
    setEntertainers(e => e.filter(x => x.id !== id));
    toast({ title: 'Entertainer deleted' });
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  const activePermits = permits.filter(p => p.status === 'active');
  const closedPermits = permits.filter(p => p.status === 'closed');
  const expiringPermits = activePermits.filter(p => p.business_days_until_expiry !== null && p.business_days_until_expiry <= 5);

  return (
    <div className="space-y-4" data-testid="dct-licensing-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">DCT Licensing</h1>
          <p className="text-gray-600 mt-1">Permit records & entertainer details</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-blue-600">{activePermits.length}</p>
          <p className="text-xs text-gray-500">Active Permits</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-amber-600">{expiringPermits.length}</p>
          <p className="text-xs text-gray-500">Expiring Soon</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-gray-400">{closedPermits.length}</p>
          <p className="text-xs text-gray-500">Closed</p>
        </CardContent></Card>
        <Card><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-purple-600">{entertainers.length}</p>
          <p className="text-xs text-gray-500">Entertainers</p>
        </CardContent></Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b pb-2">
        <button onClick={() => setTab('permits')} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${tab === 'permits' ? 'bg-blue-50 text-blue-700 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-permits">
          <ShieldAlert className="h-4 w-4 inline mr-1.5" />Permits ({permits.length})
        </button>
        <button onClick={() => setTab('entertainers')} className={`px-4 py-2 text-sm font-medium rounded-t-lg transition ${tab === 'entertainers' ? 'bg-purple-50 text-purple-700 border-b-2 border-purple-600' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-entertainers">
          <Music className="h-4 w-4 inline mr-1.5" />Entertainers ({entertainers.length})
        </button>
      </div>

      {/* ── Permits Tab ── */}
      {tab === 'permits' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-end gap-2" data-testid="permits-toolbar">
            <label className="flex items-center gap-1.5 text-xs text-gray-600 mr-1 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={includeClosed}
                onChange={e => setIncludeClosed(e.target.checked)}
                className="rounded border-gray-300"
                data-testid="include-closed-toggle"
              />
              Include closed permits
            </label>
            <Button variant="outline" size="sm" onClick={() => downloadPermits('pdf')} data-testid="permits-download-pdf">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadPermits('excel')} data-testid="permits-download-excel">
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button onClick={() => { setEditingPermit(null); setShowPermitForm(true); }} data-testid="add-permit-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Permit
            </Button>
          </div>

          {permits.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <ShieldAlert className="h-12 w-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">No permits added yet</p>
            </CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">S.No</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Permit No.</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Type</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Details</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">Issued</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">Expiry</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">Status</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {[...permits].sort((a, b) => {
                    // 1) Active permits first, closed at the bottom
                    const aActive = a.status === 'active' ? 0 : 1;
                    const bActive = b.status === 'active' ? 0 : 1;
                    if (aActive !== bActive) return aActive - bActive;
                    // 2) Within Active: nearest renewal first (expiry ASC)
                    //    Within Closed:  most recently expired first (expiry DESC)
                    const aExp = a.expiry_date || '';
                    const bExp = b.expiry_date || '';
                    if (aActive === 0) {
                      return aExp.localeCompare(bExp);
                    }
                    return bExp.localeCompare(aExp);
                  }).map((p, idx) => {
                    const bdays = p.business_days_until_expiry;
                    const isExpiring = bdays !== null && bdays <= 5 && bdays > 0 && p.status === 'active';
                    const isExpired = bdays !== null && bdays <= 0 && p.status === 'active';
                    const typeLabel = p.permit_type === 'Others' ? (p.permit_type_other || 'Others') : p.permit_type;
                    return (
                      <tr key={p.id} className={`border-b hover:bg-gray-50 ${isExpired ? 'bg-red-50' : isExpiring ? 'bg-amber-50' : ''}`} data-testid={`permit-row-${p.id}`}>
                        <td className="p-3">{idx + 1}</td>
                        <td className="p-3 font-medium">{p.permit_no}</td>
                        <td className="p-3">
                          <Badge variant="outline" className="text-xs">{typeLabel}</Badge>
                        </td>
                        <td className="p-3 text-gray-600 max-w-[200px] truncate">{p.permit_details || '--'}</td>
                        <td className="p-3 text-center">{formatDate(p.issued_date)}</td>
                        <td className="p-3 text-center">
                          <span className={isExpired ? 'text-red-600 font-semibold' : isExpiring ? 'text-amber-600 font-semibold' : ''}>
                            {formatDate(p.expiry_date)}
                          </span>
                          {isExpiring && <p className="text-[10px] text-amber-500">{bdays} business day{bdays !== 1 ? 's' : ''} left</p>}
                          {isExpired && <p className="text-[10px] text-red-500">Expired</p>}
                        </td>
                        <td className="p-3 text-center">
                          {p.status === 'active' ? (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Active</Badge>
                          ) : (
                            <Badge className="bg-gray-100 text-gray-500 text-xs">Closed</Badge>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            <button onClick={() => { setEditingPermit(p); setShowPermitForm(true); }} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                            {p.status === 'active' && (
                              <>
                                <button onClick={() => { setRenewId(p.id); setRenewDate(p.expiry_date || ''); }} className="p-1.5 rounded hover:bg-blue-50 text-blue-400 hover:text-blue-600" title="Renew"><RotateCw className="h-3.5 w-3.5" /></button>
                                <button onClick={() => closePermit(p.id)} className="p-1.5 rounded hover:bg-amber-50 text-amber-400 hover:text-amber-600" title="Close Permit"><XCircle className="h-3.5 w-3.5" /></button>
                              </>
                            )}
                            {p.status === 'closed' && (
                              <button onClick={() => reactivatePermit(p.id)} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-400 hover:text-emerald-600" title="Reactivate"><RotateCw className="h-3.5 w-3.5" /></button>
                            )}
                            <button onClick={() => deletePermit(p.id)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Entertainers Tab ── */}
      {tab === 'entertainers' && (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center justify-end gap-2" data-testid="entertainers-toolbar">
            <Button variant="outline" size="sm" onClick={() => downloadEntertainers('pdf')} data-testid="entertainers-download-pdf">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadEntertainers('excel')} data-testid="entertainers-download-excel">
              <Download className="h-4 w-4 mr-1" /> Excel
            </Button>
            <Button onClick={() => { setEditingEntertainer(null); setShowEntertainerForm(true); }} data-testid="add-entertainer-btn">
              <Plus className="h-4 w-4 mr-1" /> Add Entertainer
            </Button>
          </div>

          {entertainers.length === 0 ? (
            <Card><CardContent className="py-12 text-center">
              <Music className="h-12 w-12 mx-auto mb-3 text-gray-200" />
              <p className="text-gray-400">No entertainers added yet</p>
            </CardContent></Card>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">S.No</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Name</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Emirates ID</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">DOB</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">EID Expiry</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Passport No.</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">Passport Expiry</th>
                    <th className="p-3 text-left text-xs font-semibold text-gray-500">Place of Birth</th>
                    <th className="p-3 text-center text-xs font-semibold text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {entertainers.map((e, idx) => (
                    <tr key={e.id} className="border-b hover:bg-gray-50" data-testid={`entertainer-row-${e.id}`}>
                      <td className="p-3">{idx + 1}</td>
                      <td className="p-3 font-medium">{e.name}</td>
                      <td className="p-3">{e.emirates_id || '--'}</td>
                      <td className="p-3 text-center">{formatDate(e.dob)}</td>
                      <td className="p-3 text-center">{formatDate(e.emirates_id_expiry)}</td>
                      <td className="p-3">{e.passport_no || '--'}</td>
                      <td className="p-3 text-center">{formatDate(e.passport_expiry)}</td>
                      <td className="p-3">{e.place_of_birth || '--'}</td>
                      <td className="p-3">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => { setEditingEntertainer(e); setShowEntertainerForm(true); }} className="p-1.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600" title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => deleteEntertainer(e.id)} className="p-1.5 rounded hover:bg-red-50 text-red-400 hover:text-red-600" title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Permit Form Dialog */}
      {showPermitForm && (
        <PermitFormDialog
          editing={editingPermit}
          onClose={() => { setShowPermitForm(false); setEditingPermit(null); }}
          onSaved={(p) => {
            if (editingPermit) {
              setPermits(prev => prev.map(x => x.id === p.id ? p : x));
            } else {
              setPermits(prev => [p, ...prev]);
            }
            setShowPermitForm(false);
            setEditingPermit(null);
          }}
        />
      )}

      {/* Entertainer Form Dialog */}
      {showEntertainerForm && (
        <EntertainerFormDialog
          editing={editingEntertainer}
          onClose={() => { setShowEntertainerForm(false); setEditingEntertainer(null); }}
          onSaved={(e) => {
            if (editingEntertainer) {
              setEntertainers(prev => prev.map(x => x.id === e.id ? e : x));
            } else {
              setEntertainers(prev => [e, ...prev]);
            }
            setShowEntertainerForm(false);
            setEditingEntertainer(null);
          }}
        />
      )}

      {/* Renew Dialog */}
      {renewId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setRenewId(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 z-10" data-testid="renew-dialog">
            <h3 className="text-lg font-bold mb-4 flex items-center gap-2"><RotateCw className="h-5 w-5 text-blue-600" /> Renew Permit</h3>
            <Label>New Expiry Date</Label>
            <Input type="date" value={renewDate} onChange={e => setRenewDate(e.target.value)} className="mt-1" data-testid="renew-date" />
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" onClick={() => setRenewId(null)}>Cancel</Button>
              <Button onClick={handleRenew} disabled={!renewDate} data-testid="renew-submit">Renew</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};


// ── Permit Form Dialog ──
const PermitFormDialog = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState({
    permit_no: editing?.permit_no || '',
    permit_type: editing?.permit_type || 'DJ',
    permit_type_other: editing?.permit_type_other || '',
    permit_details: editing?.permit_details || '',
    issued_date: editing?.issued_date || '',
    expiry_date: editing?.expiry_date || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const res = await axios.patch(`${API}/api/dct-licensing/permits/${editing.id}`, form);
        onSaved(res.data);
      } else {
        const res = await axios.post(`${API}/api/dct-licensing/permits`, form);
        onSaved(res.data);
      }
      toast({ title: editing ? 'Permit updated' : 'Permit added' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 z-10" data-testid="permit-form-dialog">
        <h3 className="text-lg font-bold mb-4">{editing ? 'Edit Permit' : 'Add Permit'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label>Permit No. *</Label>
            <Input value={form.permit_no} onChange={e => setForm(f => ({ ...f, permit_no: e.target.value }))} required className="mt-1" />
          </div>
          <div>
            <Label>Permit Type *</Label>
            <Select value={form.permit_type} onValueChange={v => setForm(f => ({ ...f, permit_type: v }))}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PERMIT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          {form.permit_type === 'Others' && (
            <div>
              <Label>Specify Type</Label>
              <Input value={form.permit_type_other} onChange={e => setForm(f => ({ ...f, permit_type_other: e.target.value }))} placeholder="Enter permit type..." className="mt-1" />
            </div>
          )}
          <div>
            <Label>Permit Details</Label>
            <Textarea value={form.permit_details} onChange={e => setForm(f => ({ ...f, permit_details: e.target.value }))} rows={2} className="mt-1" placeholder="Additional details..." />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Issued Date</Label>
              <Input type="date" value={form.issued_date} onChange={e => setForm(f => ({ ...f, issued_date: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Expiry Date</Label>
              <Input type="date" value={form.expiry_date} onChange={e => setForm(f => ({ ...f, expiry_date: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.permit_no}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Permit'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};


// ── Entertainer Form Dialog ──
const EntertainerFormDialog = ({ editing, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: editing?.name || '',
    emirates_id: editing?.emirates_id || '',
    dob: editing?.dob || '',
    emirates_id_expiry: editing?.emirates_id_expiry || '',
    passport_no: editing?.passport_no || '',
    passport_expiry: editing?.passport_expiry || '',
    place_of_birth: editing?.place_of_birth || '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        const res = await axios.patch(`${API}/api/dct-licensing/entertainers/${editing.id}`, form);
        onSaved(res.data);
      } else {
        const res = await axios.post(`${API}/api/dct-licensing/entertainers`, form);
        onSaved(res.data);
      }
      toast({ title: editing ? 'Entertainer updated' : 'Entertainer added' });
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4 z-10" data-testid="entertainer-form-dialog">
        <h3 className="text-lg font-bold mb-4">{editing ? 'Edit Entertainer' : 'Add Entertainer'}</h3>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <Label>Name *</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required className="mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Emirates ID No.</Label>
              <Input value={form.emirates_id} onChange={e => setForm(f => ({ ...f, emirates_id: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Date of Birth</Label>
              <Input type="date" value={form.dob} onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>EID Expiry Date</Label>
              <Input type="date" value={form.emirates_id_expiry} onChange={e => setForm(f => ({ ...f, emirates_id_expiry: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Passport No.</Label>
              <Input value={form.passport_no} onChange={e => setForm(f => ({ ...f, passport_no: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Passport Expiry</Label>
              <Input type="date" value={form.passport_expiry} onChange={e => setForm(f => ({ ...f, passport_expiry: e.target.value }))} className="mt-1" />
            </div>
            <div>
              <Label>Place of Birth</Label>
              <Input value={form.place_of_birth} onChange={e => setForm(f => ({ ...f, place_of_birth: e.target.value }))} className="mt-1" />
            </div>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving || !form.name}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Entertainer'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DctLicensing;
